import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1460472178825-e5240623afd5?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #0d9488;
  margin: 3rem 0 1.5rem;
  font-style: italic;
  text-align: center;
`;

const InvestmentTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InvestmentCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const InvestmentTitle = styled.h3`
  font-size: 1.5rem;
  color: #0d9488;
  margin-bottom: 1rem;
  font-style: italic;
`;

const InvestmentDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
  margin-bottom: 1rem;
`;

const BenefitsList = styled.ul`
  margin: 2rem 0;
  padding-left: 1.5rem;
`;

const BenefitItem = styled.li`
  font-size: 1.1rem;
  margin-bottom: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 4rem;
  text-align: center;
`;

const Investments: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  // Investment types
  const investmentTypes = [
    {
      title: language === 'it' ? 'IMMOBILI RESIDENZIALI' : 'RESIDENTIAL PROPERTIES',
      description: language === 'it' 
        ? 'Investimenti in appartamenti, case e ville per affitti a breve e lungo termine, offrendo un flusso di reddito stabile e potenziale apprezzamento del capitale.'
        : 'Investments in apartments, houses, and villas for short and long-term rentals, offering a stable income stream and potential capital appreciation.'
    },
    {
      title: language === 'it' ? 'IMMOBILI COMMERCIALI' : 'COMMERCIAL PROPERTIES',
      description: language === 'it' 
        ? 'Opportunità di investimento in spazi commerciali, uffici e strutture al dettaglio, con contratti di locazione a lungo termine e rendimenti potenzialmente più elevati.'
        : 'Investment opportunities in commercial spaces, offices, and retail facilities, with long-term lease agreements and potentially higher returns.'
    },
    {
      title: language === 'it' ? 'FONDI IMMOBILIARI' : 'REAL ESTATE FUNDS',
      description: language === 'it' 
        ? 'Investimento in fondi gestiti professionalmente che si concentrano su vari tipi di proprietà immobiliari, consentendo la diversificazione e richiedendo un minore coinvolgimento attivo.'
        : 'Investment in professionally managed funds that focus on various types of real estate properties, allowing for diversification and requiring less active involvement.'
    },
    {
      title: language === 'it' ? 'SVILUPPO IMMOBILIARE' : 'REAL ESTATE DEVELOPMENT',
      description: language === 'it' 
        ? 'Partecipazione a progetti di sviluppo immobiliare, dalla costruzione di nuove proprietà alla ristrutturazione di immobili esistenti, offrendo potenzialmente rendimenti più elevati ma con maggiori rischi.'
        : 'Participation in real estate development projects, from constructing new properties to renovating existing ones, potentially offering higher returns but with increased risks.'
    }
  ];
  
  // Investment benefits
  const investmentBenefits = [
    language === 'it' 
      ? 'Flusso di reddito passivo attraverso affitti e apprezzamento del capitale nel tempo.'
      : 'Passive income stream through rentals and capital appreciation over time.',
    language === 'it' 
      ? 'Diversificazione del portafoglio al di là delle tradizionali azioni e obbligazioni.'
      : 'Portfolio diversification beyond traditional stocks and bonds.',
    language === 'it' 
      ? 'Copertura contro l\'inflazione, poiché i valori immobiliari e gli affitti tendono ad aumentare con l\'inflazione.'
      : 'Hedge against inflation, as real estate values and rents tend to increase with inflation.',
    language === 'it' 
      ? 'Potenziali vantaggi fiscali attraverso deduzioni e ammortamenti.'
      : 'Potential tax advantages through deductions and depreciation.',
    language === 'it' 
      ? 'Controllo tangibile su un asset fisico, a differenza di molti altri investimenti.'
      : 'Tangible control over a physical asset, unlike many other investments.'
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Investimenti | J-O-H' : 'Investments | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri opportunità di investimento immobiliare in Italia. Massimizza i tuoi rendimenti con la nostra consulenza professionale.' 
            : 'Discover real estate investment opportunities in Italy. Maximize your returns with our professional consultation.'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/property-tenant">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'INVESTIMENTI' : 'INVESTMENTS'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Esplora le opportunità di investimento immobiliare in Italia con J-O-H Offriamo consulenza professionale per aiutarti a prendere decisioni di investimento informate e massimizzare i tuoi rendimenti. Dal residenziale al commerciale, dai fondi immobiliari allo sviluppo, abbiamo soluzioni di investimento adatte alle tue esigenze e ai tuoi obiettivi finanziari.'
              : 'Explore real estate investment opportunities in Italy with J-O-H We offer professional consultation to help you make informed investment decisions and maximize your returns. From residential to commercial, from real estate funds to development, we have investment solutions suited to your needs and financial goals.'}
          </Description>
          
          <SectionTitle>{language === 'it' ? 'TIPI DI INVESTIMENTO' : 'INVESTMENT TYPES'}</SectionTitle>
          
          <InvestmentTypesGrid>
            {investmentTypes.map((type, index) => (
              <InvestmentCard key={index}>
                <InvestmentTitle>{type.title}</InvestmentTitle>
                <InvestmentDescription>{type.description}</InvestmentDescription>
              </InvestmentCard>
            ))}
          </InvestmentTypesGrid>
          
          <SectionTitle>{language === 'it' ? 'VANTAGGI DELL\'INVESTIMENTO IMMOBILIARE' : 'BENEFITS OF REAL ESTATE INVESTMENT'}</SectionTitle>
          
          <BenefitsList>
            {investmentBenefits.map((benefit, index) => (
              <BenefitItem key={index}>{benefit}</BenefitItem>
            ))}
          </BenefitsList>
          
          <Description>
            {language === 'it' 
              ? 'La nostra squadra di esperti è pronta ad assisterti in ogni fase del tuo percorso di investimento immobiliare. Dalla ricerca e analisi del mercato alla gestione del tuo portafoglio immobiliare, siamo qui per aiutarti a raggiungere i tuoi obiettivi di investimento.'
              : 'Our team of experts is ready to assist you at every stage of your real estate investment journey. From market research and analysis to managing your real estate portfolio, we are here to help you achieve your investment goals.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per una consulenza' : 'Contact us for a consultation'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Investments; 